import { TOrderItem } from 'api/orders/orders.types';
import { formatOrderPaymentJson } from '../OrderDetail/OrderDetail.utils';
import { TOrderFormValues } from './OrderForm.types';

const pad = (n: number) => String(n).padStart(2, '0');

export const isoToDatetimeLocalValue = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const datetimeLocalValueToIso = (local: string): string => {
  if (!local?.trim()) {
    return new Date().toISOString();
  }
  const parsed = new Date(local);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }
  return parsed.toISOString();
};

export const orderItemToFormValues = (order: TOrderItem): TOrderFormValues => {
  const paymentObj =
    order.payment && typeof order.payment === 'object' ? order.payment : null;
  const isManualPayment = Boolean(paymentObj?.isManualPayment);

  return {
    id: order.id,
    orderNumber: order.orderNumber,
    totalPrice: order.totalPrice,
    status: order.status,
    payed: order.payed,
    receiveNotifications: order.receiveNotifications,
    policyAccepted: order.policyAccepted,
    isManualPayment,
    manualPaymentDate:
      isManualPayment && typeof paymentObj?.paymentDate === 'string'
        ? isoToDatetimeLocalValue(paymentObj.paymentDate)
        : '',
    manualPaymentMethod:
      isManualPayment &&
      (paymentObj?.method === 'cash' || paymentObj?.method === 'bankTransfer')
        ? paymentObj.method
        : '',
    paymentJson: isManualPayment
      ? '{}'
      : formatOrderPaymentJson(order.payment) || '{}',
    userId: order.userId ?? '',
    createdAt: isoToDatetimeLocalValue(order.createdAt),
    updatedAt: isoToDatetimeLocalValue(order.updatedAt),
    shippingData: {
      ...order.shippingData,
      additionalInfo: order.shippingData.additionalInfo ?? '',
    },
    checkoutItems: order.checkoutItems.map((line) => ({
      ...line,
      stock: line.stock.map((s) => ({ ...s })),
    })),
  };
};

export const formValuesToOrderItem = (v: TOrderFormValues): TOrderItem => {
  let payment: Record<string, unknown> | null = null;
  if (v.isManualPayment) {
    payment = {
      isManualPayment: true,
      method: v.manualPaymentMethod,
      paymentDate: datetimeLocalValueToIso(v.manualPaymentDate),
    };
  } else {
    const raw = v.paymentJson?.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        payment =
          parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : { value: parsed };
      } catch {
        payment = null;
      }
    }
  }

  return {
    id: v.id.trim(),
    orderNumber: v.orderNumber.trim(),
    reservationId: v.id.trim(),
    totalPrice: v.totalPrice,
    status: v.status,
    payed: v.payed,
    receiveNotifications: v.receiveNotifications,
    policyAccepted: v.policyAccepted,
    payment,
    userId: v.userId.trim() || null,
    createdAt: datetimeLocalValueToIso(v.createdAt),
    updatedAt: datetimeLocalValueToIso(v.updatedAt),
    shippingData: {
      ...v.shippingData,
      additionalInfo:
        v.shippingData.additionalInfo?.trim() !== ''
          ? v.shippingData.additionalInfo.trim()
          : null,
    },
    checkoutItems: v.checkoutItems.map((line) => ({
      ...line,
      productId: line.productId.trim(),
      productName: line.productName.trim(),
      productImage: line.productImage.trim(),
      stock: line.stock.map((s) => ({
        name: s.name.trim(),
        variation: s.variation.trim(),
      })),
    })),
  };
};
